/* prettier-ignore */

import { inject, Injectable } from '@angular/core';
import { CookieService } from "ngx-cookie-service";
import { TranslateService } from "@ngx-translate/core";
import { RestService } from './rest.service';

enum LocaleCode {
  ES_ES = 'es-ES',
  EN_GB = 'en-GB',
  FR_FR = 'fr-FR',
  IT_IT = 'it-IT',
  DE_DE = 'de-DE',
  CH_CH = 'ch-CH',
  CA_ES = 'ca-ES',
  NL_NL = 'nl-NL',
  GL_ES = 'gl-ES',
  VA_ES = 'ca-ES-valencia',
  PT_PT = 'pt-PT',
  EU_ES = 'eu-ES',
  RU_RU = 'ru-RU',
  JA_JP = 'ja-JP',
  KO_KR = 'ko-KR',
}

@Injectable({ providedIn: 'root' })

export class GoogleTranslateService {
  private rest = inject(RestService);
  public translateService: TranslateService = inject(TranslateService);
  private cookieService: CookieService = inject(CookieService);
  public currentLang: any = this.getCookieLang() || this.rest.device?.mainLanguage || 'es';
  private mainLang = this.rest.device?.mainLanguage || 'es';
  public langFromCurrentRoute: boolean = false;

  public availableLangs = [
    { name: "Shqip", code: "sq" },
    { name: "አማርኛ", code: "am" },
    { name: "العربية", code: "ar" },
    { name: "Հայերեն", code: "hy" },
    { name: "Azərbaycanca", code: "az" },
    { name: "Euskara", code: "eu" },
    { name: "Беларуская", code: "be" },
    { name: "বাংলা", code: "bn" },
    { name: "Bosanski", code: "bs" },
    { name: "Български", code: "bg" },
    { name: "Català", code: "ca" },
    { name: "简体中文", code: "zh-CN" },
    { name: "繁體中文", code: "zh-TW" },
    { name: "Corsu", code: "co" },
    { name: "Hrvatski", code: "hr" },
    { name: "Čeština", code: "cs" },
    { name: "Dansk", code: "da" },
    { name: "Español", code: "es" },
    { name: "Nederlands", code: "nl" },
    { name: "English", code: "en" },
    { name: "Eesti", code: "et" },
    { name: "Suomi", code: "fi" },
    { name: "Français", code: "fr" },
    { name: "Galego", code: "gl" },
    { name: "Deutsch", code: "de" },
    { name: "Ελληνικά", code: "el" },
    { name: "ગુજરાતી", code: "gu" },
    { name: "Kreyòl Ayisyen", code: "ht" },
    { name: "Magyar", code: "hu" },
    { name: "Íslenska", code: "is" },
    { name: "Indonesia", code: "id" },
    { name: "Gaeilge", code: "ga" },
    { name: "Italiano", code: "it" },
    { name: "日本語", code: "ja" },
    { name: "ಕನ್ನಡ", code: "kn" },
    { name: "ខ្មែរ", code: "km" },
    { name: "Kinyarwanda", code: "rw" },
    { name: "한국어", code: "ko" },
    { name: "Кыргызча", code: "ky" },
    { name: "Latina", code: "la" },
    { name: "Latviešu", code: "lv" },
    { name: "Lietuvių", code: "lt" },
    { name: "Lëtzebuergesch", code: "lb" },
    { name: "Македонски", code: "mk" },
    { name: "Malagasy", code: "mg" },
    { name: "Bahasa Melayu", code: "ms" },
    { name: "മലയാളം", code: "ml" },
    { name: "Malti", code: "mt" },
    { name: "मराठी", code: "mr" },
    { name: "Монгол", code: "mn" },
    { name: "မြန်မာ", code: "my" },
    { name: "नेपाली", code: "ne" },
    { name: "Norsk", code: "no" },
    { name: "پښتو", code: "ps" },
    { name: "Polski", code: "pl" },
    { name: "Português", code: "pt" },
    { name: "ਪੰਜਾਬੀ", code: "pa" },
    { name: "Română", code: "ro" },
    { name: "Русский", code: "ru" },
    { name: "Gagana Sāmoa", code: "sm" },
    { name: "Gàidhlig", code: "gd" },
    { name: "Српски", code: "sr" },
    { name: "Sesotho", code: "st" },
    { name: "Shona", code: "sn" },
    { name: "سنڌي", code: "sd" },
    { name: "සිංහල", code: "si" },
    { name: "Slovenčina", code: "sk" },
    { name: "Slovenščina", code: "sl" },
    { name: "Soomaali", code: "so" },
    { name: "Svenska", code: "sv" },
    { name: "Tagalog", code: "tl" },
    { name: "Тоҷикӣ", code: "tg" },
    { name: "Татарча", code: "tt" },
    { name: "ไทย", code: "th" },
    { name: "Türkçe", code: "tr" },
    { name: "Türkmençe", code: "tk" },
    { name: "Українська", code: "uk" },
    { name: "ئۇيغۇرچە", code: "ug" },
    { name: "Oʻzbekcha", code: "uz" },
    { name: "Tiếng Việt", code: "vi" },
    { name: "Cymraeg", code: "cy" }
  ];

  constructor() { }

  /**
   * Obtiene el dominio base actual de forma dinámica.
   * Funciona con cualquier dominio (ciceroneweb.com, emtmadrid.es, localhost, etc.)
   */
  private getCurrentDomain(): string {
    const hostname = window.location.hostname;
    // Para localhost, devolver localhost directamente
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'localhost';
    }
    // Para otros dominios, obtener el dominio base (ej: emtmadrid.es de smartourism.emtmadrid.es)
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return `.${parts.slice(-2).join('.')}`;
    }
    return hostname;
  }

  /**
   * Obtiene el dominio completo actual (incluyendo subdominio)
   */
  private getFullDomain(): string {
    return window.location.hostname;
  }

  getCookieLang() {
    const cookie = this.cookieService.get('googtrans')
    if (!cookie) return null;
    const cookieSplit = cookie.split('/');
    return cookieSplit?.[cookieSplit.length - 1];
  }

  deleteCookieLang() {
    const baseDomain = this.getCurrentDomain();
    const fullDomain = this.getFullDomain();

    // Eliminar cookies para el dominio actual
    this.cookieService.delete('googtrans', '/', baseDomain);
    this.cookieService.delete('googtrans', '/', fullDomain);
    this.cookieService.deleteAll('/');

    // Eliminar cookies directamente con document.cookie
    document.cookie = `googtrans=; path=/; domain=${baseDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    document.cookie = `googtrans=; path=/; domain=${fullDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    document.cookie = "googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";

    location.reload();
  }

  setLang(lang: string) {
    this.setCookieLang(lang);
    this.setLocalLang(lang);
    location.reload();
  }

  setCookieLang(lang) {
    const baseDomain = this.getCurrentDomain();
    const fullDomain = this.getFullDomain();

    // Eliminar cookies existentes para el dominio actual
    this.cookieService.delete('googtrans', '/', baseDomain);
    this.cookieService.delete('googtrans', '/', fullDomain);
    this.cookieService.deleteAll('/');

    console.log(`Setting googtrans cookie for domains: ${baseDomain}, ${fullDomain}`);

    // Establecer cookies para el dominio actual
    document.cookie = `googtrans=/${this.mainLang}/${lang}; path=/; domain=${baseDomain}; expires=Thu, 01 Jan 2030 00:00:00 UTC`;
    document.cookie = `googtrans=/${this.mainLang}/${lang}; path=/; domain=${fullDomain}; expires=Thu, 01 Jan 2030 00:00:00 UTC`;
    // Cookie sin dominio específico (para el dominio actual)
    document.cookie = `googtrans=/${this.mainLang}/${lang}; path=/; expires=Thu, 01 Jan 2030 00:00:00 UTC`;

    console.log("Setting lang from cookie: ", lang);
  }

  setRandomLang() {
    const langs = this.availableLangs.map(lang => lang.code);
    const randomLang = langs[Math.floor(Math.random() * langs.length)];
    this.setLang(randomLang)
  }

  setLocalLang(lang: string) {
    this.cookieService.delete('locallang');
    // this.cookieService.set('locallang', lang);
    this.cookieService.set(
      'locallang',
      lang,
      undefined, // expires
      '/',       // path
      undefined, // domain
      true,      // secure
      'Lax',     // sameSite
      false      // partitioned
    );
  }

  getLocalLang() {
    const cookie = this.cookieService.get('locallang')
    if (!cookie || cookie === '') return null
    return cookie;
  }

  removeLocalLang() {
    this.cookieService.delete('locallang');
  }

  changeLanguage(lang) {
    this.currentLang = lang
    this.translateService.setDefaultLang(lang);
    this.translateService.use(lang);
    this.setLocalLang(lang);
  }

  changeLangOnInit(lang, apiLangs = ['es', 'en']) {
    if (!lang)
      return;

    const currentCookieLang = this.getCookieLang();
    if (apiLangs?.includes(lang)) {
      if (currentCookieLang)
        this.deleteCookieLang();

      this.changeLanguage(lang);
      return;
    }

    if (currentCookieLang === lang)
      return;

    const foundLang = this.availableLangs?.find(e => e.code === lang);
    if (!foundLang)
      return;

    this.setLang(lang);
  }

  cookieLangMapping(lang: string) {
    switch (lang) {
      case 'es':
        return LocaleCode.ES_ES
      case 'en':
        return LocaleCode.EN_GB
      case 'fr':
        return LocaleCode.FR_FR
      case 'it':
        return LocaleCode.IT_IT
      case 'de':
        return LocaleCode.DE_DE
      case 'ch':
        return LocaleCode.CH_CH
      case 'ca':
        return LocaleCode.CA_ES
      case 'nl':
        return LocaleCode.NL_NL
      case 'gl':
        return LocaleCode.GL_ES
      case 'va':
        return LocaleCode.VA_ES
      case 'pt':
        return LocaleCode.PT_PT
      case 'eu':
        return LocaleCode.EU_ES
      case 'ru':
        return LocaleCode.RU_RU
      case 'ja':
        return LocaleCode.JA_JP
      case 'ko':
        return LocaleCode.KO_KR
      default:
        return LocaleCode.ES_ES
    }
  }
}

